import { useMemo, useState, type CSSProperties } from 'react';
import { color, chaseTheme, redcapRuleTheme } from '../styles/theme';
import { dateTime, num, plainDate } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { useAdmins } from '../api/queries';
import type { RedcapCell, RedcapChase, RedcapRuleKey, RedcapWeek as RedcapWeekSummary } from '../api/client';
import { RECORD_ROWS_OPTIONS, RULE_OPTIONS, ruleLabel, useSetInstrumentRule, useUpdateChase } from '../api/redcap';
import type { RedcapTab } from './RedcapSteps';
import { Button, Card, CardTitle, ColHead, Confirm, Meter, selectStyle } from '../components/ui';
import { Note, Pill, PointerCard, problemOf } from './RedcapShared';

/**
 * The counts, the chases and the weeks on record — the pieces of the weekly
 * extract that the "This week" page keeps as its Details. Moved here from
 * Redcap.tsx unchanged in behaviour, except that the rule control also sets
 * whether a form's late records are listed or counted (044), and a week in
 * the history can be picked.
 */

/**
 * Weekly completeness before the study is linked. The project used to be
 * linkable here too, under the words "switch on" — the same action Project
 * setup calls "link", which read as two settings. One place links it now,
 * and it is step 1.
 */
export function LinkFirst({ name, canWrite, onGo }: { name: string; canWrite: boolean; onGo: (tab: RedcapTab) => void }) {
  return (
    <PointerCard
      title={`No weekly report for ${name} yet`}
      actionLabel={canWrite ? 'Go to Project setup' : undefined}
      onAction={canWrite ? () => onGo('setup') : undefined}
    >
      The Monday file comes from the study’s own REDCap project, so it has to be linked first — step 1, under Project
      setup.{canWrite ? '' : ' Someone with the REDCap permission can link it.'} Once it is, this tab takes the weekly
      file and lists what to talk about.
    </PointerCard>
  );
}

/* ─────────────────────────────────────────────────────────────── counts */

const RULE_FILTERS: ReadonlyArray<[RedcapRuleKey | 'all', string]> = [
  ['all', 'Every rule'],
  ...RULE_OPTIONS.map((r) => [r.key, r.label] as [RedcapRuleKey, string]),
];

/** Whether an incomplete count is one this study watches. */
function watched(cell: RedcapCell): boolean {
  return cell.nIncomplete > 0 && (cell.rule === 'chase' || cell.rule === 'tint' || (cell.rule === 'held' && cell.windowClosed));
}

export function changeText(cell: RedcapCell, hadPrevious: boolean): string {
  if (!cell.previous) return hadPrevious ? 'New this week' : '';
  const d = cell.nIncomplete - cell.previous.nIncomplete;
  if (d === 0) return 'Same as last week';
  return d < 0 ? `${Math.abs(d)} fewer than last week` : `${d} more than last week`;
}

function RuleControl({ slug, cell, canWrite }: { slug: string; cell: RedcapCell; canWrite: boolean }) {
  const set = useSetInstrumentRule();
  const rule = cell.rule;
  const chases = rule === 'chase' || rule === 'held';

  if (!canWrite) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Pill tone={redcapRuleTheme[rule]}>{ruleLabel(rule)}</Pill>
        {rule === 'held' && (
          <span style={{ fontSize: 11.5, color: color.muted }}>{cell.windowClosed ? 'window closed' : 'window open'}</span>
        )}
        {chases && (
          <span style={{ fontSize: 11.5, color: color.muted }}>{cell.recordRows === 'list' ? 'records listed' : 'records counted'}</span>
        )}
      </span>
    );
  }

  const small = { ...selectStyle, fontSize: 12.5, padding: '6px 28px 6px 10px' } as const;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <select
        aria-label={`Rule for ${cell.label}`}
        value={rule}
        disabled={set.isPending}
        title={RULE_OPTIONS.find((r) => r.key === rule)?.blurb}
        onChange={(e) =>
          set.mutate({ slug, instrument: cell.instrument, rule: e.target.value as RedcapRuleKey, windowClosed: false })
        }
        style={small}
      >
        {RULE_OPTIONS.map((r) => (
          <option key={r.key} value={r.key}>
            {r.label}
          </option>
        ))}
      </select>
      {rule === 'held' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: color.muted, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={cell.windowClosed}
            disabled={set.isPending}
            onChange={(e) => set.mutate({ slug, instrument: cell.instrument, rule: 'held', windowClosed: e.target.checked })}
          />
          Window closed
        </label>
      )}
      {chases && (
        // Whether the week names the records behind this form or only counts them. The AE form
        // counts by default: "AE incomplete for record 1023" says an event happened to 1023.
        <select
          aria-label={`Late records for ${cell.label}`}
          value={cell.recordRows}
          disabled={set.isPending}
          title={RECORD_ROWS_OPTIONS.find((r) => r.key === cell.recordRows)?.blurb}
          onChange={(e) =>
            set.mutate({
              slug,
              instrument: cell.instrument,
              rule,
              windowClosed: cell.windowClosed,
              recordRows: e.target.value as RedcapCell['recordRows'],
            })
          }
          style={{ ...small, fontWeight: 600, color: color.muted }}
        >
          {RECORD_ROWS_OPTIONS.map((r) => (
            <option key={r.key} value={r.key}>
              Records {r.label.toLowerCase()}
            </option>
          ))}
        </select>
      )}
      {set.error && (
        <span role="alert" style={{ fontSize: 11.5, color: color.red }}>
          {problemOf(set.error).message}
        </span>
      )}
    </span>
  );
}

const CELL_GRID = 'minmax(0,1.5fr) minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,0.7fr)';

/** The week's counts per visit and form, with last week's beside them, and the rule each form follows. */
export function CountsTable({
  slug,
  asOf,
  previousAsOf,
  cells,
  canWrite,
}: {
  slug: string;
  asOf: string;
  previousAsOf: string | null;
  cells: RedcapCell[];
  canWrite: boolean;
}) {
  const [event, setEvent] = useState<string>('all');
  const [rule, setRule] = useState<RedcapRuleKey | 'all'>('all');

  const events = useMemo(() => [...new Set(cells.map((c) => c.eventName))], [cells]);
  const rows = cells.filter((c) => (event === 'all' || c.eventName === event) && (rule === 'all' || c.rule === rule));
  const incomplete = cells.reduce((n, c) => n + c.nIncomplete, 0);
  const hadPrevious = previousAsOf !== null;

  const chip = (active: boolean): CSSProperties => ({
    font: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    padding: '5px 12px',
    borderRadius: 999,
    cursor: 'pointer',
    border: `1px solid ${active ? color.navy : color.line}`,
    background: active ? color.navy : color.white,
    color: active ? color.white : color.ink,
  });

  return (
    <Card pad={0}>
      <div style={{ padding: '18px 16px 12px' }}>
        <CardTitle style={{ marginBottom: 4 }}>Counts · week of {plainDate(asOf)}</CardTitle>
        <Note>
          {num(cells.length)} {cells.length === 1 ? 'form count' : 'form counts'}, {num(incomplete)} not complete in all
          {previousAsOf ? `, compared with the week of ${plainDate(previousAsOf)}` : ''}. The rule decides what a form left
          incomplete sets off, and whether the week names the records behind it.
        </Note>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {['all', ...events].map((ev) => (
            <button key={ev} onClick={() => setEvent(ev)} style={chip(event === ev)}>
              {ev === 'all' ? 'Every visit' : ev}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {RULE_FILTERS.map(([key, label]) => (
            <button key={key} onClick={() => setRule(key)} style={chip(rule === key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="x-scroll">
        <div style={{ minWidth: 760 }}>
          <div style={{ display: 'grid', gridTemplateColumns: CELL_GRID, gap: 12, padding: '0 16px 8px' }}>
            <ColHead>Visit · form</ColHead>
            <ColHead>Complete</ColHead>
            <ColHead>Not complete</ColHead>
            <ColHead>Rule</ColHead>
            <ColHead>Chase</ColHead>
          </div>
          {rows.length === 0 && (
            <p style={{ padding: '14px 16px', margin: 0, fontSize: 13.5, color: color.muted }}>Nothing matches these filters.</p>
          )}
          {rows.map((c) => {
            const pct = c.nExpected > 0 ? Math.round((c.nComplete / c.nExpected) * 100) : 0;
            const hot = watched(c);
            return (
              <div
                key={`${c.eventName}|${c.instrument}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: CELL_GRID,
                  gap: 12,
                  alignItems: 'center',
                  padding: '11px 16px',
                  borderTop: `1px solid ${color.line}`,
                  fontSize: 13.5,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: color.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.label}
                  </div>
                  <div style={{ fontSize: 12, color: color.muted }}>
                    {c.eventName}
                    {c.sourceName.toLowerCase() !== c.label.toLowerCase() ? ` · ${c.sourceName}` : ''}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: color.ink, fontVariantNumeric: 'tabular-nums', marginBottom: 5 }}>
                    {num(c.nComplete)}/{num(c.nExpected)}
                  </div>
                  <Meter pct={pct} height={6} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: hot ? '#8a4f16' : color.ink }}>
                    {num(c.nIncomplete)}
                  </div>
                  <div style={{ fontSize: 11.5, color: color.muted }}>{changeText(c, hadPrevious)}</div>
                </div>
                <RuleControl slug={slug} cell={c} canWrite={canWrite} />
                <div>{c.chaseId ? <Pill tone={chaseTheme.open}>Chasing</Pill> : <span style={{ color: color.muted }}>—</span>}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────── chases */

function chaseState(c: RedcapChase): { tone: { bg: string; fg: string }; label: string } {
  if (c.status === 'open') return c.overdue ? { tone: chaseTheme.overdue, label: 'Overdue' } : { tone: chaseTheme.open, label: 'Open' };
  if (c.status === 'done') return { tone: chaseTheme.done, label: 'Done' };
  return { tone: chaseTheme.resolved, label: 'Cleared by the file' };
}

const CHASE_GRID = 'minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.2fr) auto';

function ChaseRow({ c, slug, canWrite }: { c: RedcapChase; slug: string; canWrite: boolean }) {
  const { admin, can } = useAuth();
  const admins = useAdmins(canWrite && can('admins:read'));
  const update = useUpdateChase();
  const [confirm, setConfirm] = useState(false);
  const state = chaseState(c);

  // Only an active account that can see this study is offered: handing a
  // chase to someone the study is hidden from puts it out of their sight.
  const eligible = (admins.data?.admins ?? []).filter(
    (a) => a.status === 'active' && (a.role === 'owner' || a.studies.includes(slug)),
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: CHASE_GRID,
        gap: 12,
        alignItems: 'center',
        padding: '11px 16px',
        borderTop: `1px solid ${color.line}`,
        fontSize: 13.5,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: color.navy }}>
          Close {c.label} at {c.eventName}
        </div>
        <div style={{ fontSize: 12, color: color.muted }}>
          {num(c.nIncomplete)} not complete
          {c.openedAsOf ? ` · opened with the week of ${plainDate(c.openedAsOf)}` : ''}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        <Pill tone={state.tone}>{state.label}</Pill>
        <span style={{ fontSize: 11.5, color: color.muted }}>
          {c.status === 'open'
            ? `Due ${plainDate(c.dueOn)}`
            : c.closedAt
              ? `${c.closedByName ? `${c.closedByName} · ` : ''}${dateTime(c.closedAt)}`
              : ''}
        </span>
      </div>

      <div style={{ minWidth: 0 }}>
        {canWrite && c.status === 'open' && admins.data ? (
          <select
            aria-label={`Owner of ${c.label} at ${c.eventName}`}
            value={c.ownerId ?? ''}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ id: c.id, ownerId: e.target.value || null })}
            style={{ ...selectStyle, fontSize: 12.5, padding: '6px 28px 6px 10px' }}
          >
            <option value="">Unassigned</option>
            {c.ownerId && !eligible.some((a) => a.id === c.ownerId) && (
              <option value={c.ownerId}>{c.ownerName ?? 'Current owner'}</option>
            )}
            {eligible.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        ) : canWrite && c.status === 'open' && admin ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: c.ownerName ? color.ink : color.muted }}>{c.ownerName ?? 'Unassigned'}</span>
            {c.ownerId === admin.id ? (
              <Button variant="ghost" onClick={() => update.mutate({ id: c.id, ownerId: null })} style={{ padding: '5px 12px', fontSize: 12 }}>
                Release
              </Button>
            ) : (
              <Button variant="soft" onClick={() => update.mutate({ id: c.id, ownerId: admin.id })} style={{ padding: '5px 12px', fontSize: 12 }}>
                Take it
              </Button>
            )}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: c.ownerName ? color.ink : color.muted }}>{c.ownerName ?? 'Unassigned'}</span>
        )}
        {update.error && (
          <div role="alert" style={{ fontSize: 11.5, color: color.red, marginTop: 4 }}>
            {problemOf(update.error).message}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {canWrite && c.status === 'open' && (
          <Confirm
            open={confirm}
            title="Mark this chase done?"
            detail="It stays closed for this week. If next Monday’s file still shows the form incomplete, a new chase opens."
            confirmLabel="Mark done"
            align="right"
            onConfirm={() => {
              setConfirm(false);
              update.mutate({ id: c.id, status: 'done' });
            }}
            onCancel={() => setConfirm(false)}
          >
            <Button onClick={() => setConfirm(true)} disabled={update.isPending} style={{ padding: '7px 14px', fontSize: 12 }}>
              Mark done
            </Button>
          </Confirm>
        )}
        {canWrite && c.status !== 'open' && (
          <Button
            variant="ghost"
            onClick={() => update.mutate({ id: c.id, status: 'open' })}
            disabled={update.isPending}
            style={{ padding: '7px 14px', fontSize: 12 }}
          >
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}

export function Chases({ chases, slug, canWrite }: { chases: RedcapChase[]; slug: string; canWrite: boolean }) {
  const open = chases.filter((c) => c.status === 'open');
  const closed = chases.filter((c) => c.status !== 'open');

  return (
    <Card pad={0}>
      <div style={{ padding: '18px 16px 12px' }}>
        <CardTitle style={{ marginBottom: 4 }}>Chases</CardTitle>
        <Note>
          A chase opens when a form whose rule chases is not complete two Mondays running, and is due by the next
          Monday’s file. A later file that shows it complete clears it. Closed chases stay here for four weeks.
        </Note>
      </div>
      {chases.length === 0 && (
        <p style={{ padding: '14px 16px 18px', margin: 0, fontSize: 13.5, color: color.muted }}>Nothing to chase.</p>
      )}
      {chases.length > 0 && (
        <div className="x-scroll">
          <div style={{ minWidth: 680 }}>
            <div style={{ display: 'grid', gridTemplateColumns: CHASE_GRID, gap: 12, padding: '0 16px 8px' }}>
              <ColHead>What</ColHead>
              <ColHead>State</ColHead>
              <ColHead>Owner</ColHead>
              <span />
            </div>
            {[...open, ...closed].map((c) => (
              <ChaseRow key={c.id} c={c} slug={slug} canWrite={canWrite} />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────── history */

const WEEK_GRID = 'minmax(0,1.2fr) minmax(0,0.9fr) minmax(0,0.7fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.6fr)';

/** Every week on record, newest first. A row can be picked to open that week's page. */
export function History({
  weeks,
  picked,
  onPick,
}: {
  weeks: RedcapWeekSummary[];
  picked?: string | null;
  onPick?: (asOf: string) => void;
}) {
  if (weeks.length === 0) return null;
  return (
    <Card pad={0}>
      <div style={{ padding: '18px 16px 12px' }}>
        <CardTitle style={{ marginBottom: 4 }}>Weeks on record</CardTitle>
        <Note>
          Newest first. Totals are across every visit and form.{onPick ? ' Pick a week to open its page.' : ''}
        </Note>
      </div>
      <div className="x-scroll">
        <div style={{ minWidth: 720 }}>
          <div style={{ display: 'grid', gridTemplateColumns: WEEK_GRID, gap: 12, padding: '0 16px 8px' }}>
            <ColHead>Week of</ColHead>
            <ColHead>File</ColHead>
            <ColHead>Rows</ColHead>
            <ColHead>Complete</ColHead>
            <ColHead>Not complete</ColHead>
            <ColHead>Uploaded</ColHead>
          </div>
          {weeks.map((w, i) => {
            const older = weeks[i + 1];
            const d = older ? w.incomplete - older.incomplete : null;
            const on = picked === w.asOf;
            const row = (
              <>
                <span style={{ fontWeight: 700, color: on ? color.blueDeep : color.navy }}>{plainDate(w.asOf)}</span>
                <span style={{ color: color.muted, fontSize: 12.5 }}>
                  {w.source === 'status' ? `Status export${w.recordCount !== null ? ` · ${num(w.recordCount)} records` : ''}` : 'Counts file'}
                  <span style={{ display: 'block', fontSize: 11.5 }}>Dictionary {w.dictionaryVersion}</span>
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{num(w.cells)}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {num(w.complete)}/{num(w.expected)}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {num(w.incomplete)}
                  {d !== null && d !== 0 && (
                    <span style={{ color: color.muted, fontSize: 11.5 }}> ({d > 0 ? `+${d}` : `−${Math.abs(d)}`})</span>
                  )}
                </span>
                <span style={{ color: color.muted, fontSize: 12 }}>
                  {dateTime(w.uploadedAt)}
                  {w.uploadedByName ? ` · ${w.uploadedByName}` : ''}
                  {w.replacedAt ? ' · re-uploaded' : ''}
                </span>
              </>
            );
            const style: CSSProperties = {
              display: 'grid',
              gridTemplateColumns: WEEK_GRID,
              gap: 12,
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: `1px solid ${color.line}`,
              fontSize: 13,
              width: '100%',
              textAlign: 'left',
              background: on ? color.tint : 'transparent',
            };
            return onPick ? (
              <button
                key={w.id}
                onClick={() => onPick(w.asOf)}
                aria-pressed={on}
                className={on ? undefined : 'hov-tint'}
                style={{ ...style, border: 'none', cursor: 'pointer', font: 'inherit', color: color.ink }}
              >
                {row}
              </button>
            ) : (
              <div key={w.id} style={style}>
                {row}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
